import { usePage } from "@inertiajs/react"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

/** What came of the brief that came through signup (INCO-470). */
interface ClaimOutcome {
  state: "added" | "waiting" | "unavailable" | "gated"
  title: string | null
  origin: string | null
  /**
   * The new desk's own portal, where the brief now is. Only when it is —
   * or, behind an NCNDA, the brief's own page, where the gate is (INCO-483).
   */
  path?: string | null
}

/**
 * The first thing a reader sees on a desk they just created to hold a brief
 * (INCO-470): the brief is on the portal, it is waiting on the origin, or it
 * is no longer available. A modal rather than a toast, because they are no
 * longer on the brief to read one, and this is the whole reason the desk
 * exists.
 *
 * One heading for all three. What they did was start a desk; whether the
 * brief made it is the sentence under it, not the news.
 */
export function ClaimOutcomeModal() {
  const outcome = usePage<{ claim_outcome?: ClaimOutcome | null }>().props.claim_outcome ?? null
  const [open, setOpen] = useState(Boolean(outcome))

  useEffect(() => {
    setOpen(Boolean(outcome))
  }, [outcome])

  if (!outcome) return null

  const title = outcome.title ?? "The brief"
  const origin = outcome.origin ?? "the desk it came from"

  const body =
    outcome.state === "added"
      ? `Your desk is ready, and "${title}" has been added to your portal.`
      : outcome.state === "waiting"
        ? `Your desk is ready. ${origin} has been asked to approve adding "${title}" to your desk. An email will be sent when they answer, and the brief can be published to your portal once approved.`
        : outcome.state === "gated"
          ? `Your desk is ready. ${origin} asks readers to accept its NCNDA before "${title}" can be added. Accept it on the brief's page, then add the brief from there.`
          : `Your desk is ready, but "${title}" was not added. ${origin} closed or withdrew it during sign-up.`

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Welcome to Inco</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
          {outcome.path && (
            <Button asChild>
              <a href={outcome.path}>{outcome.state === "gated" ? "Open brief" : "Open portal"}</a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
